import { useState, useCallback, useEffect } from 'react'
import { getBaseUnitId } from '../utils/unitConversionUtils'

const STORAGE_KEY = 'ioag-messages-unit-overrides'

/**
 * Unit overrides type: maps unit category ID to selected unit ID
 */
export type UnitOverrides = Record<number, number>

/**
 * Hook for managing unit display overrides in the Messages page.
 *
 * Stores user selections for unit display preferences per unit category.
 * Persists selections to localStorage for cross-session persistence.
 *
 * @returns Object with:
 *   - unitOverrides: Current unit override selections (categoryId -> unitId)
 *   - setUnitOverride: Function to set a specific unit override
 *   - resetUnitOverrides: Function to reset all overrides to defaults
 */
export function useMessageUnitOverrides(): {
  unitOverrides: UnitOverrides
  setUnitOverride: (categoryId: number, unitId: number) => void
  resetUnitOverrides: () => void
} {
  // Initialize state from localStorage
  const [unitOverrides, setUnitOverrides] = useState<UnitOverrides>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        return JSON.parse(stored) as UnitOverrides
      }
    } catch (e) {
      console.warn('Failed to parse unit overrides from localStorage:', e)
    }
    return {}
  })

  // Persist to localStorage whenever overrides change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(unitOverrides))
    } catch (e) {
      console.warn('Failed to save unit overrides to localStorage:', e)
    }
  }, [unitOverrides])

  // Set a specific unit override
  const setUnitOverride = useCallback((categoryId: number, unitId: number) => {
    setUnitOverrides((prev) => {
      // If setting to base unit, remove the override (base is the default)
      const baseUnitId = getBaseUnitId(categoryId)
      if (unitId === baseUnitId) {
        const { [categoryId]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [categoryId]: unitId }
    })
  }, [])

  // Reset all overrides to defaults (base units)
  const resetUnitOverrides = useCallback(() => {
    setUnitOverrides({})
  }, [])

  return {
    unitOverrides,
    setUnitOverride,
    resetUnitOverrides,
  }
}
